/**
 * @fileoverview Derive the page layout combinations a letterhead variant
 * produces (which header / footer / left / right asset shows on page 1,
 * on later pages, and on even pages when an odd/even rule is in play).
 * The variant editor lists one margins row per combination.
 */

import {
  layoutKeyForPage,
  layoutKeysEqual,
  type PageLayoutKey,
  type VariantFragments,
} from "./resolve-letterhead";

export type LayoutComboPageClass = "first" | "other" | "even";

export interface LayoutCombo {
  key: PageLayoutKey;
  pageClass: LayoutComboPageClass;
  /** e.g. "Page 1 · Header + Footer + Right side" */
  title: string;
}

const PAGE_CLASS_LABEL: Record<LayoutComboPageClass, string> = {
  first: "Page 1",
  other: "Other pages",
  even: "Even pages",
};

/**
 * Representative 1-based page for each layout class. Page rules only
 * distinguish first / other / even pages: page 1 is "first", page 3 is the
 * plain "other" page (odd, not first), page 2 is the "even" page, which
 * differs from page 3 only under odd/even rules.
 */
export const PAGE_FOR_CLASS: Record<LayoutComboPageClass, number> = {
  first: 1,
  other: 3,
  even: 2,
};

/**
 * Human-readable list of the zones present in a key. With `assetNames`
 * (asset id → library name) each zone reads as the actual letterhead the
 * variant's rules put on that page, e.g. "NEW NAME 2 — Header".
 */
export function describeLayoutKey(
  key: PageLayoutKey,
  assetNames?: Record<string, string>,
): string {
  const label = (id: string | null, zone: string) =>
    id ? (assetNames?.[id] ?? zone) : null;
  const zones = [
    label(key.header, "Header"),
    label(key.footer, "Footer"),
    label(key.leftSide, "Left side"),
    label(key.rightSide, "Right side"),
  ].filter((z): z is string => !!z);
  return zones.length > 0 ? zones.join(" + ") : "No letterhead";
}

export function derivePageLayoutCombos(
  fragments: VariantFragments[],
  assetNames?: Record<string, string>,
): LayoutCombo[] {
  const probes: LayoutComboPageClass[] = ["first", "other", "even"];
  const out: LayoutCombo[] = [];
  for (const pageClass of probes) {
    const key = layoutKeyForPage(fragments, PAGE_FOR_CLASS[pageClass]);
    if (out.some((c) => layoutKeysEqual(c.key, key))) continue;
    out.push({
      key,
      pageClass,
      title: `${PAGE_CLASS_LABEL[pageClass]} · ${describeLayoutKey(key, assetNames)}`,
    });
  }
  return out;
}
